// import * as React from 'react';
import React, { useRef } from 'react';
import { IData } from './types';
import FieldGroup from './components/FieldGroup';
import Results from './components/Results';
import parseToNumber from './utils/parseToNumber';
import calculateResults from './utils/calculateResults';
import extractInitialValues from './utils/extractInitialValues';
import DiagonalArrow from './components/icons/DiagonalArrow';
import { useFormContext } from './components/contexts/form';
import { postData } from './externals/postData';
import { submitRecaptcha } from './externals/submitRecaptcha';

// Define a type for the possible HTML elements you'll ref
type FormFieldElement =
	| HTMLInputElement
	| HTMLTextAreaElement
	| HTMLSelectElement
	| HTMLButtonElement;

function App(data: IData) {
	const calculatorKey = data.calculatorKey;
	const [results, setResults] = React.useState<{ [key: string]: number }>({});
	const [showResults, setShowResults] = React.useState(false);
	const [postID, setPostID] = React.useState('');
	const [generalError, setGeneralError] = React.useState('');
	const { formData, setFormData, setErrors, setLoading, loading, errors } = useFormContext();

	const entryurl = new URL(window.location.href);

	if (postID) {
		entryurl.searchParams.append('entry', postID);
	}

	// A single ref to hold an object where keys are field names and values are the DOM elements.
	const formFieldRefs = useRef<Record<string, FormFieldElement | null>>({});

	/**
	 * A helper function to assign the ref to the specific field
	 * This is called by the `ref` prop on each element
	 *
	 * @param el The form field element to reference, or `null` if the element is unmounted.
	 */
	const setFieldRef = (el: FormFieldElement | null) => {
		if (el?.name) {
			// Ensure element exists and has a name attribute
			formFieldRefs.current[el.name] = el;
		}
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		setLoading(true);
		setErrors({});
		setGeneralError('');

		const newErrors: { [key: string]: boolean } = {};

		Object.keys(formData).forEach((key) => {
			if (!formData[key]) {
				newErrors[key] = true;
			}
		});

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			setGeneralError('Please fill out all the fields');
			setLoading(false);

			// Get the field name of the very first field with error.
			const firstErrorFieldName = Object.keys(newErrors)[0];

			// Get the field of the very first field with error in the formFieldRefs.
			const fieldElement = formFieldRefs.current[firstErrorFieldName];
			if (fieldElement) {
				fieldElement.focus();
				fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}

			return;
		}

		try {
			const parsedData = parseToNumber(formData);
			const postResults = calculateResults(-1, parsedData, calculatorKey);
			if ('object' !== typeof postResults) return;

			const url = data.apiBase;

			const recaptchaToken = await submitRecaptcha(verify.siteKey);
			const response = await postData({ ...postResults, recaptchaToken, calculatorKey }, url);

			setLoading(false);
			setPostID(response.post_id);
			entryurl.searchParams.append('entry', response.post_id);

			setResults(parsedData);
			setShowResults(true);

			const inputField = document.querySelector<HTMLInputElement>(
				'input[id^="field_roi-calc-associated-post"]'
			);
			const shareInputField = document.querySelector<HTMLInputElement>(
				'input[id^="field_roi-calc-share-associated-post"]'
			);

			if (inputField) {
				inputField.value = entryurl.href;
			}
			if (shareInputField) {
				shareInputField.value = entryurl.href;
			}
		} catch (error) {
			setLoading(false);
			if (error instanceof Error) {
				setErrors({ submission: error.message });
			} else {
				setErrors({ submission: 'Failed to submit data' });
			}
		}
	};

	const handleShare = () => {
		const gateForm = document.querySelector<HTMLElement>('.rs-calc-gate');
		if (gateForm) {
			if (!gateForm.classList.contains('rs-calc-gate--active')) {
				gateForm.classList.add('rs-calc-gate--active');
			}
			const gateFormURL = gateForm.querySelector<HTMLElement>('.rs-calc-gate__share-url');
			if (gateFormURL) {
				gateFormURL.innerHTML = entryurl.href;
				gateFormURL.setAttribute('data-clipboard-text', entryurl.href);
			}
		}
	};

	React.useEffect(() => {
		const initialValues = extractInitialValues(data);
		setFormData(initialValues);
		setResults(parseToNumber(initialValues));
		if (data.showResults) {
			setShowResults(true);

			const inputField = document.querySelector<HTMLInputElement>(
				'input[id^="field_roi-calc-associated-post"]'
			);
			const shareInputField = document.querySelector<HTMLInputElement>(
				'input[id^="field_roi-calc-share-associated-post"]'
			);

			if (inputField) {
				inputField.value = entryurl.href;
			}
			if (shareInputField) {
				shareInputField.value = entryurl.href;
			}
		}
	}, [data]);

	return (
		<div className="row">
			<div className="app-calculator__left col-lg-5">
				<form onSubmit={handleSubmit}>
					<div className="app-calculator__inner">
						<p className="app-calculator__heading has-text-align-center">
							{data.calcHeading}
						</p>
						{data.calcFields.map((fields, index) => (
							<FieldGroup key={index} fieldGroup={fields} setFieldRef={setFieldRef} />
						))}
						<div className="app-calculator__button-container">
							{errors &&
								Object.keys(errors).length > 0 &&
								Object.keys(errors).map((key) => (
									<p key={key} className="app-calculator__error">
										{errors[key]}
									</p>
								))}

							{generalError && (
								<p className="app-calculator__error">{generalError}</p>
							)}
							<button className="btn btn-primary" type="submit" disabled={loading}>
								Calculate <DiagonalArrow />
							</button>
						</div>
					</div>
				</form>
			</div>
			<div className="app-calculator__right col-lg-7">
				<div className="app-calculator__inner">
					<p className="t4 has-text-align-center">{data.resultHeading}</p>
					<Results
						resultHeadings={data.resultHeadings}
						formData={results}
						showResults={showResults}
						calculatorKey={data.calculatorKey}
					/>
					<div className="app-calculator__results-button-container">
						<button
							className="btn btn-primary"
							onClick={handleShare}
							disabled={!showResults || loading}
						>
							Share Results <DiagonalArrow />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default App;
